import produce from 'immer';
import _ from 'underscore';

import {assert} from 'Asserts';
import {AddFromStructuredTablePasteOption} from 'notebook/sample-propagation/AddFromStructuredTableConstants';
import remergeStructuredTableSingleInputs from 'notebook/structured-tables/single-inputs/util/remergeStructuredTableSingleInputs';
import {
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableRow,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableHelper from 'notebook/structured-tables/StructuredTableHelper';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import TableUtil from 'notebook/tables/TableUtil';
import notifications from 'notifications';

export interface AddToTableOptions {
  pasteOption?: AddFromStructuredTablePasteOption;
}

/**
 * This routine moves samples into the table in the given columns. The samples must already
 * be formatted as Cell objects, as this function (ironically) doesn't know anything about
 * samples, only tables.
 *
 * The type parameters are inferred. Simply pass in a structured table, and you'll get back
 * a new structured table of the same type.
 *
 * The first row that receives new cells is the immediately after the last row with data
 * in any of the specified columns.
 *
 * In most cases, your `samplesByColumn` array should have a single entry. If you need to
 * insert tuples (e.g., plate/position pairs when propagating wells), you'll pass multiple
 * entries. They must match, as described by the assertions below.
 *
 * New rows are added as needed. If the size of the new table would exceed ELN_MAX_STRUCTURED_TABLE_ROWS,
 * we return NULL.
 */
export default function addSamplesToTable<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  // we need the actual full table to determine if any cells are locked
  table: Table,
  samplesByColumn: Array<{samples: Array<Cell>; columnIndex: number}>,
  {pasteOption = AddFromStructuredTablePasteOption.APPEND}: AddToTableOptions = {}
): {updatedTable: StructuredTable<TTableMetadata, TRowMetadata>; tableOp: TableOpDiff} | null {
  assert(samplesByColumn.length > 0, 'No samples were selected for insertion');
  assert(
    _.unique(samplesByColumn.map(({samples}) => samples.length)).length === 1,
    'When adding samples into multiple columns of a table, each set of samples must be the same length',
    {samplesByColumn}
  );
  assert(
    _.unique(samplesByColumn.map(({columnIndex}) => columnIndex)).length === samplesByColumn.length,
    'When adding samples into multiple columns of a table, each column index must be unique',
    {samplesByColumn}
  );

  const tableOp: TableOpDiff = {type: 'upsert-cells', cells: [], newRowIndexes: []};
  const numCellSetsToAdd = samplesByColumn[0].samples.length;
  let indexOfCellsBeingAdded = 0;

  const destinationColumnIndexes = samplesByColumn.map(({columnIndex}) => columnIndex);
  let rows: Array<StructuredTableRow<TRowMetadata>> = [...structuredTable.rows];
  const isReplace = pasteOption === AddFromStructuredTablePasteOption.REPLACE;
  // Insert the samples into columns of existing rows, until we run out of rows (or samples)
  let insertAtRow = isReplace
    ? 0
    : 1 + findLastFilledCellInColumns(structuredTable, destinationColumnIndexes);
  rows = produce(rows, (draftRows) => {
    while (insertAtRow < draftRows.length && indexOfCellsBeingAdded < numCellSetsToAdd) {
      // if any cell in the row is locked, skip that row
      if (!isRowArchived(table, insertAtRow)) {
        for (const {samples, columnIndex} of samplesByColumn) {
          draftRows[insertAtRow].cells[columnIndex] = samples[indexOfCellsBeingAdded];
          tableOp.cells.push({row: insertAtRow, column: columnIndex});
        }
        indexOfCellsBeingAdded++;
      }
      insertAtRow++;
    }
    // if we're doing replace and there are no more samples to add, clear out all remaining rows
    if (isReplace && indexOfCellsBeingAdded === numCellSetsToAdd) {
      while (insertAtRow < draftRows.length) {
        // except for locked rows, skip those
        if (!isRowArchived(table, insertAtRow)) {
          for (const {columnIndex} of samplesByColumn) {
            StructuredTableHelper._clearCell(draftRows[insertAtRow].cells[columnIndex]);
            tableOp.cells.push({row: insertAtRow, column: columnIndex});
          }
        }
        insertAtRow++;
      }
    }
  });

  // If we have more samples to add, append rows and fill them
  while (indexOfCellsBeingAdded < numCellSetsToAdd) {
    const newRow = structuredTableAdapter.getBlankRow(
      structuredTable.metadata,
      insertAtRow++,
      structuredTable.columns
    );
    for (const {samples, columnIndex} of samplesByColumn) {
      newRow.cells[columnIndex] = samples[indexOfCellsBeingAdded];
    }
    rows.push(newRow);
    tableOp.newRowIndexes!.push(rows.length - 1);
    indexOfCellsBeingAdded++;
  }

  const capacityErrorMsg = structuredTableAdapter.getTableLengthErrorMessage(
    structuredTable,
    rows.length,
    true
  );
  if (capacityErrorMsg != null) {
    notifications.error(capacityErrorMsg);
    return null;
  }

  let updatedStructuredTable: StructuredTable<TTableMetadata, TRowMetadata> = {
    ...structuredTable,
    rows,
  };
  /** Since we're adding rows, try to remerge  */
  updatedStructuredTable = remergeStructuredTableSingleInputs(updatedStructuredTable);
  return {updatedTable: updatedStructuredTable, tableOp};
}

/**
 * Calculate the number of samples that can be added through UAS without adding new rows to the table.
 * This allows us to block the addition of new rows via UAS, if needed
 */
export function maximumNumberOfSamplesThatCanBeAddedBeforeExpansion(
  structuredTable: UnknownStructuredTable,
  destinationColumnIndices: Array<number>
): number {
  const firstEmptyRowIndex = 1 + findLastFilledCellInColumns(structuredTable, destinationColumnIndices);
  return structuredTable.rows.length - firstEmptyRowIndex;
}

// Returns the row index of the last filled cell in any of the given columns, or -1 if all cells are blank
function findLastFilledCellInColumns(
  structuredTable: UnknownStructuredTable,
  columnIndices: Array<number>
): number {
  const {rows, columns} = structuredTable;
  let rowIndex = rows.length - 1;
  while (rowIndex >= 0) {
    const isRowEmpty = (rowIdx: number): boolean =>
      columnIndices.every((columnIndex) => {
        const cell = rows[rowIdx].cells[columnIndex];
        // For Single Inputs, ignore the mergeRowSpan and mergeColSpan to consider it empty
        if (columns[columnIndex].isSingleInput) {
          const {mergeRowSpan, mergeColSpan, ...cellWithoutMerges} = cell;
          return TableUtil.isCellEmpty(cellWithoutMerges);
        }
        return TableUtil.isCellEmpty(cell);
      });

    if (!isRowEmpty(rowIndex)) {
      return rowIndex;
    }
    --rowIndex;
  }

  return -1;
}

// Surpisingly, we need to check the actual rendered table to see if rows are archived, we don't store that
// information on the StructuredTable object itself
function isRowArchived(table: Table, rowIndex: number): boolean {
  return table.rowData?.[rowIndex]?.uiData?.archivePurpose != null;
}
